// Global Instructions Rule Applied!
import { useState, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

/**
 * One country option, as CountrySelect consumes it: the select shape plus the cfg country row spread in.
 * @typedef {{ label: string, value: string, code: string, name: string, [countryField: string]: unknown }} CountryOption
 */
/** @type {CountryOption[]} */
const NO_COUNTRIES = []

/**
 * Custom hook for local country search using Redux state
 * Performs client-side filtering with lowercase partial matching
 *
 * @returns the hook API — searchCountries, lookupCountryByCode, loading, results, clearResults, hasSearched (shape inferred from the returned literal)
 */
export const useCountryLocalSearch = () => {
  const [results, setResults] = useState(NO_COUNTRIES)
  const [hasSearched, setHasSearched] = useState(false)

  // Get countries data from globalConfigs Redux state
  const countriesData = useSelector((state) => state.globalConfigs?.countries || [])

  // Create local countriesByCode lookup for efficient code-based access
  const countriesByCode = useMemo(() => {
    const lookup = {}
    countriesData.forEach((country) => {
      if (country.code) {
        lookup[country.code.toLowerCase()] = country
      }
    })
    return lookup
  }, [countriesData])

  // Transform countries data into options format for Select component
  const countryOptions = useMemo(() => {
    return countriesData.map((country) => ({
      label: country.name,
      value: country.code, // Use code as value for backend compatibility
      code: country.code,
      name: country.name,
      ...country // Include all original country data
    }))
  }, [countriesData])

  // Local search function with lowercase partial matching
  const searchCountries = useCallback(
    (query) => {
      const trimmedQuery = query?.trim() || ''

      // Clear results if query is empty or too short
      if (!trimmedQuery || trimmedQuery.length < 2) {
        setResults([])
        setHasSearched(false)
        return
      }

      // Perform case-insensitive partial search
      const searchTerm = trimmedQuery.toLowerCase()
      const filteredCountries = countryOptions.filter(
        (country) =>
          country.label.toLowerCase().includes(searchTerm) ||
          (country.code && country.code.toLowerCase().includes(searchTerm))
      )

      // Limit results to 20 items for performance (same as API)
      const limitedResults = filteredCountries.slice(0, 20)

      setResults(limitedResults)
      setHasSearched(true)
    },
    [countryOptions]
  )

  // Look up country by code function
  const lookupCountryByCode = useCallback(
    (countryCode) => {
      if (!countryCode || countryCode.length !== 2) {
        return Promise.resolve(null)
      }

      // Use countriesByCode lookup for efficient access
      const country =
        countriesByCode[countryCode.toLowerCase()] ||
        countriesData.find((c) => c.code?.toLowerCase() === countryCode.toLowerCase())

      if (country) {
        return Promise.resolve({
          label: country.name,
          value: country.code, // This is the country code (e.g., "US")
          code: country.code,
          name: country.name,
          ...country
        })
      }

      return Promise.resolve(null)
    },
    [countriesData, countriesByCode]
  )

  // Clear results function
  const clearResults = useCallback(() => {
    setResults([])
    setHasSearched(false)
  }, [])

  return {
    searchCountries,
    lookupCountryByCode,
    loading: false, // No loading since we're using local data
    error: null, // No API errors since we're using local data
    results,
    clearResults,
    hasSearched
  }
}
